import { createContext, PropsWithChildren, useContext } from "react";
import { CaptchaConfigProvider } from "../../utils/captcha/captcha-config-context.tsx";

export interface LoginConfigContextStore {
  captchaSiteKey: string;
  facebookAppId: string;
  googleClientId: string;
  appleClientId: string;
  appleRedirectUri: string;
}

export const LoginConfigContext = createContext<LoginConfigContextStore | undefined>(undefined);

export const useLoginConfig = () => useContext(LoginConfigContext);

/*
 * Provides the login configuration to the components that need it.
 * Expects server configuration in 'globalThis.serverConfig.login'
 * If a context was already provided it's a noop
 *
 * Note: This provider wraps children with CaptchaConfigProvider for backward compatibility.
 * Components that only need captcha config can use CaptchaConfigProvider directly.
 */
export const LoginConfigProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const context = useContext(LoginConfigContext);
  const loginConfig = globalThis.serverConfig?.login;

  if (loginConfig === undefined) {
    if (context === undefined) {
      console.warn("LoginConfigProvider: no 'serverConfig.login' found and no parent context. Context is not set.");
    }
    return <>{children}</>;
  }

  const config: LoginConfigContextStore = {
    captchaSiteKey: loginConfig.captchaSiteKey,
    googleClientId: loginConfig.googleClientId,
    facebookAppId: loginConfig.facebookAppId,
    appleClientId: loginConfig.appleClientId,
    appleRedirectUri: loginConfig.appleRedirectUri,
  };

  return (
    <CaptchaConfigProvider>
      <LoginConfigContext.Provider value={config}>{children}</LoginConfigContext.Provider>
    </CaptchaConfigProvider>
  );
};
